import React, { useEffect, useState, useContext } from "react";
import {
  doc,
  getDoc,
  getDocs,
  collection,
  query,
  where,
  setDoc,
} from "firebase/firestore";
import { db } from "../../firebase";
import { StudentContext } from "../../contexts/StudentContext";
import { Link } from "react-router-dom";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

const DailyModule = () => {
  const { studentData } = useContext(StudentContext);
  const [state, setState] = useState({
    courseInfo: null,
    courseLoading: true,
    courseError: null,
    days: [],
    userSubmissions: {},
    currentAccessibleDay: localStorage.getItem(`currentAccessibleDay_${studentData?.id}`)
      ? parseInt(localStorage.getItem(`currentAccessibleDay_${studentData?.id}`), 10)
      : 1,
    courseStartDate: null,
    currentPage: 1,
  });

  const { courseInfo, courseLoading, courseError, days, userSubmissions, currentAccessibleDay, courseStartDate, currentPage } = state;
  const DAYS_PER_PAGE = 30;
  const CACHE_TTL = 30 * 60 * 1000; // 5 minutes

  // Pagination logic
  const totalPages = Math.ceil(days.length / DAYS_PER_PAGE);
  const startIdx = (currentPage - 1) * DAYS_PER_PAGE;
  const endIdx = startIdx + DAYS_PER_PAGE;
  const paginatedDays = days.slice(startIdx, endIdx);

  // Helper: Normalize courseStartDate
  const normalizeCourseStartDate = (date) => {
    if (!date) {
      console.warn("No courseStartDate, defaulting to today");
      return new Date();
    }
    if (date.toDate) {
      const d = date.toDate();
      return isNaN(d.getTime()) ? new Date() : d;
    }
    const parsed = new Date(date);
    return isNaN(parsed.getTime()) ? new Date() : parsed;
  };

  // Helper: Get unlock date for a day
  const getUnlockDate = (dayNumber, startDate) => {
    if (!startDate) {
      console.warn("No startDate for unlockDate, using today");
      return new Date();
    }
    const unlockDate = new Date(startDate);
    unlockDate.setDate(startDate.getDate() + (dayNumber - 1));
    unlockDate.setHours(0, 0, 0, 0);
    return unlockDate;
  };

  // Helper: Get cached data
  const getCachedData = (key) => {
    const cached = localStorage.getItem(key);
    if (cached) {
      const { data, timestamp } = JSON.parse(cached);
      if (Date.now() - timestamp < CACHE_TTL) {
        return data;
      }
      localStorage.removeItem(key);
    }
    return null;
  };

  // Helper: Set cached data
  const setCachedData = (key, data) => {
    localStorage.setItem(key, JSON.stringify({ data, timestamp: Date.now() }));
  };

  // Fetch user submissions
  const fetchUserSubmissions = async (courseId) => {
    if (!studentData?.id) {
      console.warn("No student ID for submissions");
      return {};
    }
    const cacheKey = `submissions_${studentData.id}_${courseId}`;
    const cached = getCachedData(cacheKey);
    if (cached) {
      console.log("Using cached submissions:", cached);
      return cached;
    }

    const answersRef = collection(db, "answers");
    const q = query(
      answersRef,
      where("studentId", "==", studentData.id),
      where("courseId", "==", courseId)
    );
    const querySnapshot = await getDocs(q);
    const submissions = {};
    querySnapshot.forEach((doc) => {
      const data = doc.data();
      submissions[data.dayId] = { ...data, docId: doc.id };
    });
    setCachedData(cacheKey, submissions);
    console.log("Fetched submissions:", submissions);
    return submissions;
  };

  // Calculate current accessible day
  const calculateCurrentAccessibleDay = (daysData, submissions, startDate) => {
    let highestSubmittedDay = 0;
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const normalizedStartDate = new Date(startDate);
    normalizedStartDate.setHours(0, 0, 0, 0);

    console.log("Today:", today, "Course Start Date:", normalizedStartDate);

    if (normalizedStartDate > today) {
      console.log("Course in future, Day 1");
      return 1;
    }

    if (normalizedStartDate.getTime() === today.getTime() && !Object.keys(submissions).length) {
      console.log("Course starts today, no submissions, Day 1");
      return 1;
    }

    for (let i = 0; i < daysData.length; i++) {
      const dayData = daysData[i];
      const dayNumber = i + 1;
      const submission = submissions[dayData.id];
      if (submission) {
        const unlockDate = getUnlockDate(dayNumber, startDate);
        console.log(`Day ${dayNumber}: Submission exists, unlockDate: ${unlockDate}`);
        if (today >= unlockDate) {
          highestSubmittedDay = Math.max(highestSubmittedDay, dayNumber);
        }
      }
    }

    const nextDay = highestSubmittedDay + 1;
    console.log(`Highest Submitted Day: ${highestSubmittedDay}, Next Day: ${nextDay}`);
    return nextDay;
  };

  const fetchCourse = async () => {
    const startTime = performance.now();
    try {
      if (!studentData?.uid) {
        throw new Error("Student UID not available");
      }

      setState((prev) => ({ ...prev, courseLoading: true, courseError: null }));

      // Check cache
      const cacheKey = `course_${studentData.uid}`;
      const cachedCourse = getCachedData(cacheKey);
      if (cachedCourse) {
        console.log("Loaded from cache:", cachedCourse);
        setState((prev) => ({
          ...prev,
          courseInfo: cachedCourse.courseInfo,
          days: cachedCourse.days,
          userSubmissions: cachedCourse.submissions,
          courseStartDate: new Date(cachedCourse.courseStartDate),
          currentAccessibleDay: cachedCourse.currentAccessibleDay,
          courseLoading: false,
        }));
        return;
      }

      // Parallel fetches
      const studentRef = doc(db, "students", studentData.uid);
      const [studentDoc, courseDoc, daysSnapshot, submissions] = await Promise.all([
        getDoc(studentRef),
        studentData.course ? getDoc(doc(db, "courses", studentData.course)) : Promise.resolve(null),
        studentData.course ? getDocs(collection(db, `courses/${studentData.course}/day`)) : Promise.resolve({ docs: [] }),
        studentData.course ? fetchUserSubmissions(studentData.course) : Promise.resolve({}),
      ]);

      if (!studentDoc.exists()) {
        throw new Error("Student not found");
      }

      const studentDataFromDoc = studentDoc.data();
      const startDate = normalizeCourseStartDate(studentDataFromDoc.courseStartDate);
      console.log("Normalized courseStartDate:", startDate);

      if (!courseDoc?. exists()) {
        throw new Error("Course not found");
      }

      const courseData = { ...courseDoc.data(), id: studentData.course };
      const daysData = daysSnapshot.docs
        .map((doc) => ({ id: doc.id, ...doc.data() }))
        .sort((a, b) => {
          const aNum = parseInt(a.id.replace(/\D/g, "")) || 0;
          const bNum = parseInt(b.id.replace(/\D/g, "")) || 0;
          return aNum - bNum;
        });

      console.log("Fetched days:", daysData);

      const accessibleDay = calculateCurrentAccessibleDay(daysData, submissions, startDate);
      localStorage.setItem(`currentAccessibleDay_${studentData?.id}`, accessibleDay.toString());

      // Update current_day_status in Firebase
      try {
        await setDoc(
          doc(db, "students", studentData.uid),
          { current_day_status: accessibleDay },
          { merge: true }
        );
        console.log(`Updated current_day_status to ${accessibleDay} for student ${studentData.uid}`);
      } catch (error) {
        console.error("Error updating current_day_status:", error);
        toast.error(`Failed to update current day status: ${error.message}`);
      }

      const generatedDays = daysData.map((doc, i) => {
        const dayNumber = i + 1;
        const submission = submissions[doc.id];
        const isAccessible = dayNumber <= accessibleDay;
        const isCurrentDay = dayNumber === accessibleDay;
        const isCompleted = submission && (submission.status === "Approved" || doc.autoApproval);

        return {
          id: doc.id,
          day: dayNumber,
          date: `Day ${dayNumber}`,
          dayTitle: doc.dayTitle || "No Title",
          assignmentRequired: doc.assignmentRequired || false,
          autoApproval: doc.autoApproval || false,
          manualApproval: doc.manualApproval || false,
          status: submission?.status || null,
          completed: isCompleted,
          isCurrentDay: isCurrentDay,
          isAccessible: isAccessible,
          isLocked: !isAccessible,
          submission: submission,
          canProgress: isCompleted || (isCurrentDay && !doc.assignmentRequired),
        };
      });

      // Cache data
      setCachedData(cacheKey, {
        courseInfo: courseData,
        days: generatedDays,
        submissions,
        courseStartDate: startDate.toISOString(),
        currentAccessibleDay: accessibleDay,
      });

      // Update state in one go
      setState((prev) => ({
        ...prev,
        courseInfo: courseData,
        days: generatedDays,
        userSubmissions: submissions,
        courseStartDate: startDate,
        currentAccessibleDay: accessibleDay,
        courseLoading: false,
      }));

      toast.success("Progress synced successfully!");
      console.log(`Fetch took ${(performance.now() - startTime).toFixed(2)}ms`);
    } catch (error) {
      console.error("Fetch error:", error);
      setState((prev) => ({
        ...prev,
        courseError: error.message,
        courseLoading: false,
      }));
      toast.error(`Error syncing progress: ${error.message}`);
    }
  };

  useEffect(() => {
    if (studentData?.uid) {
      fetchCourse();
    }
  }, [studentData]);

  // Refresh every minute, but only fetch if necessary
  useEffect(() => {
    const interval = setInterval(() => {
      if (days.length > 0 && Object.keys(userSubmissions).length > 0 && courseStartDate) {
        const daysData = days.map((d) => ({
          id: d.id,
          assignmentRequired: d.assignmentRequired,
          autoApproval: d.autoApproval,
          manualApproval: d.manualApproval,
          dayTitle: d.dayTitle,
        }));
        const newAccessibleDay = calculateCurrentAccessibleDay(daysData, userSubmissions, courseStartDate);
        if (newAccessibleDay !== currentAccessibleDay) {
          setState((prev) => ({
            ...prev,
            currentAccessibleDay: newAccessibleDay,
          }));
          localStorage.setItem(`currentAccessibleDay_${studentData?.id}`, newAccessibleDay.toString());
          // Update current_day_status in Firebase on day change
          if (studentData?.uid) {
            setDoc(
              doc(db, "students", studentData.uid),
              { current_day_status: newAccessibleDay },
              { merge: true }
            )
              .then(() => console.log(`Updated current_day_status to ${newAccessibleDay} for student ${studentData.uid}`))
              .catch((error) => {
                console.error("Error updating current_day_status:", error);
                toast.error(`Failed to update current day status: ${error.message}`);
              });
          }
          fetchCourse(); // Only re-fetch if the day changes
        }
      }
    }, 60000);
    return () => clearInterval(interval);
  }, [days, userSubmissions, currentAccessibleDay, studentData?.id, studentData?.uid, courseStartDate]);

  const DayBox = ({
    day,
    date,
    title,
    completed,
    isCurrentDay,
    isAccessible,
    isLocked,
    submission,
    dayData,
  }) => {
    const styles = {
      completed: {
        background: "linear-gradient(135deg, #d1fae5 0%, #a7f3d0 100%)",
        border: "1.5px solid #34d399",
        boxShadow: "0 4px 16px rgba(34,197,94,0.12)",
        cursor: "pointer",
      },
      currentDay: {
        background: "linear-gradient(135deg, #dbeafe 0%, #bfdbfe 100%)",
        border: "1.5px solid #3b82f6",
        boxShadow: "0 4px 16px rgba(59,130,246,0.15)",
        cursor: "pointer",
      },
      locked: {
        background: "linear-gradient(135deg, #f1f5f9 0%, #e2e8f0 100%)",
        border: "1.5px solid #cbd5e1",
        boxShadow: "0 2px 8px rgba(0,0,0,0.04)",
        cursor: "not-allowed",
        opacity: 0.6,
      },
     rejected: {
        background: "linear-gradient(135deg, #fee2e2 0%, #fecaca 100%)",
        border: "1.5px solid #f87171",
        boxShadow: "0 4px 16px rgba(248,113,113,0.12)",
        cursor: "pointer",
      },
      rejected: {
        background: "linear-gradient(135deg, #fee2e2 0%, #fecaca 100%)",
        border: "1.5px solid #f87171",
        boxShadow: "0 4px 16px rgba(248,113,113,0.12)",
        cursor: "pointer",
      },
      pending: {
        background: "linear-gradient(135deg, #fefce8 0%, #fef3c7 100%)",
        border: "1.5px solid #f59e0b",
        boxShadow: "0 4px 16px rgba(245,158,11,0.12)",
        cursor: "pointer",
      },
    };

    const getBoxStyle = () => {
      if (submission?.status === "Rejected") return styles.rejected;
      if (submission?.status === "Pending" || (submission && !submission.status)) return styles.pending;
      if (isLocked) return styles.locked;
      if (completed) return styles.completed;
      if (isCurrentDay) return styles.currentDay;
      return styles.locked;
    };

    const boxStyle = {
      borderRadius: 12,
      padding: "20px 24px",
      width: 210,
      height: 260,
      position: "relative",
      transition: "all 0.3s ease",
      display: "flex",
      flexDirection: "column",
      justifyContent: "flex-start",
      ...getBoxStyle(),
    };

    const getStatusIcon = () => {
      if (completed) return <i className="bi bi-check-circle-fill" style={{ color: "#22c55e", fontSize: 20 }} />;
      if (submission?.status === "Rejected") return <i className="bi bi-x-circle-fill" style={{ color: "#ef4444", fontSize: 20 }} />;
      if (submission?.status === "Pending" || (submission && !submission.status)) return <i className="bi bi-clock-fill" style={{ color: "#f59e0b", fontSize: 18 }} />;
      if (isCurrentDay) return <i className="bi bi-play-circle-fill" style={{ color: "#3b82f6", fontSize: 20 }} />;
      if (isLocked) return <i className="bi bi-lock-fill" style={{ color: "#64748b", fontSize: 18 }} />;
      return null;
    };

    const getStatusText = () => {
      if (completed) return "Completed";
      if (submission?.status === "Rejected") return "Rejected - Contact Instructor";
      if (submission?.status === "Pending" || (submission && !submission.status)) return "Pending Review";
      if (isCurrentDay) return "Available Now";
      if (isLocked) return "Locked";
      return "";
    };

    const handleClick = (e) => {
      if (isLocked) {
        e.preventDefault();
        if (day === currentAccessibleDay) {
          const prevDay = days.find((d) => d.day === day - 1);
          if (prevDay && prevDay.assignmentRequired && !prevDay.submission) {
            toast.warning(`Submit assignment for Day ${prevDay.day} to proceed.`);
          } else {
            toast.info(`Complete Day ${prevDay.day} to unlock.`);
          }
        } else {
          toast.warning("Complete previous days to unlock.");
        }
      }
    };

    const BoxContent = (
      <div style={boxStyle} onClick={handleClick}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 12 }}>
          <div style={{ display: "flex", flexDirection: "column", alignItems: "flex-start" }}>
            <span style={{ fontWeight: 600, fontSize: 18, color: "#1f2937" }}>Day {day}</span>
            <span style={{ color: "#6b7280", fontSize: 13, marginTop: 2 }}>{date}</span>
          </div>
          <div style={{ display: "flex", flexDirection: "column", alignItems: "center" }}>
            {getStatusIcon()}
          </div>
        </div>
        <hr style={{ margin: "12px 0", border: "none", borderTop: "1px solid rgba(0,0,0,0.1)" }} />
        <div style={{
          fontWeight: 600,
          fontSize: 15,
          color: "#1f2937",
          marginBottom: 8,
          minHeight: 40,
          display: "-webkit-box",
          WebkitLineClamp: 2,
          WebkitBoxOrient: "vertical",
          overflow: "hidden",
        }}>
          {title}
        </div>
        <div style={{ marginTop: "auto", paddingTop: 8 }}>
          <div style={{
            fontSize: 12,
            fontWeight: 500,
            color: completed ? "#059669" :
                   isCurrentDay ? "#2563eb" :
                   submission?.status === "Rejected" ? "#dc2626" :
                   submission?.status === "Pending" ? "#d97706" : "#64748b",
            textAlign: "center",
            padding: "4px 8px",
            borderRadius: 6,
            backgroundColor: completed ? "rgba(34,197,94,0.1)" :
                           isCurrentDay ? "rgba(59,130,246,0.1)" :
                           submission?.status === "Rejected" ? "rgba(239,68,68,0.1)" :
                           submission?.status === "Pending" ? "rgba(245,158,11,0.1)" : "rgba(100,116,139,0.1)",
            marginBottom: 4,
          }}>
            {getStatusText()}
          </div>
          {dayData?.assignmentRequired && (
            <div style={{
              fontSize: 11,
              color: "#6b7280",
              textAlign: "center",
              marginTop: 4,
              fontStyle: "italic",
            }}>
              Assignment Required
            </div>
          )}
        </div>
      </div>
    );

    if (isAccessible) {
      return (
        <Link
          to="/dailycoursemodule"
          state={{ dayId: dayData?.id, courseId: courseInfo?.id || studentData?.course }}
          style={{ textDecoration: "none", color: "inherit" }}
        >
          {BoxContent}
        </Link>
      );
    }
    return BoxContent;
  };

  if (courseLoading) {
    return (
      <div style={{
        display: "flex",
        flexDirection: "column",
        justifyContent: "center",
        alignItems: "center",
        height: "100vh",
        backgroundColor: "#f9fafb",
        color: "#6b7280",
        fontSize: 18,
      }}>
        <div style={{
          border: "4px solid #e5e7eb",
          borderTop: "4px solid #3b82f6",
          borderRadius: "50%",
          width: 40,
          height: 40,
          animation: "spin 1s linear infinite",
        }} />
        <style>{`@keyframes spin { 0% { transform: rotate(0deg); } 100% { transform: rotate(360deg); } }`}</style>
        <p style={{ marginTop: 16 }}>Loading your course progress...</p>
      </div>
    );
  }

  if (courseError) {
    return (
      <div style={{
        textAlign: "center",
        padding: 40,
        color: "#dc2626",
        backgroundColor: "#fee2e2",
        borderRadius: 12,
        margin: 20,
      }}>
        <i className="bi bi-exclamation-triangle" style={{ fontSize: 24, marginBottom: 12 }} />
        <div>Error: {courseError}</div>
      </div>
    );
  }

  if (!courseInfo && !studentData?.course) {
    return (
      <div style={{ textAlign: "center", padding: 40, color: "#dc2626" }}>
        Error: Course data not available
      </div>
    );
  }

  if (!days || days.length === 0) {
    return (
      <div style={{
        textAlign: "center",
        padding: 40,
        color: "#6b7280",
        backgroundColor: "#f9fafb",
        borderRadius: 12,
        margin: 20,
      }}>
        <i className="bi bi-calendar-x" style={{ fontSize: 48, marginBottom: 16, display: "block" }} />
        No assignments available for this course
      </div>
    );
  }

  return (
    <div style={{ maxWidth: 1200, margin: "32px auto", padding: "0 20px" }}>
      <ToastContainer position="top-right" autoClose={4000} hideProgressBar={false} newestOnTop={false} closeOnClick rtl={false} pauseOnFocusLoss draggable pauseOnHover />
      <div style={{
        background: "linear-gradient(135deg, #7c3aed 0%, #ec4899 100%)",
        borderRadius: 16,
        padding: "32px 36px",
        marginBottom: 32,
        color: "white",
        boxShadow: "0 8px 32px rgba(124, 58, 237, 0.12)",
      }}>
        <h2 style={{ margin: 0, fontWeight: 700, fontSize: 32, letterSpacing: "-0.5px", marginBottom: 8 }}>
          {courseInfo?.title || "Course Title"}
        </h2>
        <div style={{ fontSize: 18, fontWeight: 500, opacity: 0.9, marginBottom: 4 }}>Daily Progress</div>
        <div style={{ fontSize: 16, opacity: 0.8 }}>
          You are on Day {currentAccessibleDay} • {days.filter((d) => d.completed).length} of {days.length} days completed
        </div>
      </div>
      <div style={{
        display: "grid",
        gridTemplateColumns: "repeat(auto-fit, minmax(210px, 1fr))",
        gap: 20,
        background: "#fff",
        borderRadius: 16,
        padding: 28,
        boxShadow: "0 4px 24px rgba(0, 0, 0, 0.06)",
        border: "1px solid #f1f5f9",
      }}>
        {paginatedDays.map((d) => (
          <DayBox
            key={d.day}
            day={d.day}
            date={d.date}
            title={d.dayTitle}
            completed={d.completed}
            isCurrentDay={d.isCurrentDay}
            isAccessible={d.isAccessible}
            isLocked={d.isLocked}
            submission={d.submission}
            dayData={d}
          />
        ))}
      </div>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginTop: 24, padding: "16px 0" }}>
        <div style={{ color: "#6b7280", fontSize: 14 }}>
          Showing {paginatedDays.length === 0 ? 0 : startIdx + 1} to {Math.min(endIdx, days.length)} of {days.length} days
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
          <button
            style={{
              padding: "8px 16px",
              backgroundColor: currentPage === 1 ? "#f1f5f9" : "#fff",
              border: "1px solid #d1d5db",
              borderRadius: 8,
              color: currentPage === 1 ? "#9ca3af" : "#374151",
              cursor: currentPage === 1 ? "not-allowed" : "pointer",
              fontSize: 14,
            }}
            onClick={() => setState((prev) => ({ ...prev, currentPage: prev.currentPage - 1 }))}
            disabled={currentPage === 1}
          >
            Previous
          </button>
          <span style={{ fontSize: 14, color: "#6b7280" }}>Page {currentPage} of {totalPages || 1}</span>
          <button
            style={{
              padding: "8px 16px",
              backgroundColor: currentPage === totalPages || totalPages === 0 ? "#f1f5f9" : "#fff",
              border: "1px solid #d1d5db",
              borderRadius: 8,
              color: currentPage === totalPages || totalPages === 0 ? "#9ca3af" : "#374151",
              cursor: currentPage === totalPages || totalPages === 0 ? "not-allowed" : "pointer",
              fontSize: 14,
            }}
            onClick={() => setState((prev) => ({ ...prev, currentPage: prev.currentPage + 1 }))}
            disabled={currentPage === totalPages || totalPages === 0}
          >
            Next
          </button>
        </div>
      </div>
    </div>
  );
};

export default DailyModule;